/** 役職マスタ(hr:admin)。rank が小さいほど上位で、上長の自動導出はこの並びで決まる。 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { Position } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useHrPerms, usePositions } from "./hooks";

type FormValues = { name: string; rank: number };

export function PositionsPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { canAdmin } = useHrPerms();
  const [editing, setEditing] = useState<Position | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const query = usePositions();

  const open = creating || editing !== null;
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing) form.setFieldsValue({ name: editing.name, rank: editing.rank });
  }, [open, editing, form]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  };

  const save = useMutation({
    mutationFn: (v: FormValues) =>
      editing ? api.updatePosition(editing.id, v) : api.createPosition(v),
    onSuccess: () => {
      void message.success(editing ? "更新した" : "作成した");
      setCreating(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.updatePosition(id, { active }),
    onSuccess: () => invalidate(),
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<Position> = [
    { title: "役職", width: 160, dataIndex: "name" },
    { title: "rank(小さいほど上位)", dataIndex: "rank", width: 214 },
    {
      title: "状態",
      dataIndex: "active",
      width: 110,
      render: (v: boolean) => (v ? <Tag color="green">稼働中</Tag> : <Tag>停止中</Tag>),
    },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 160,
            render: (_: unknown, row: Position) => (
              <Space>
                <Button size="small" onClick={() => setEditing(row)}>
                  編集
                </Button>
                <Switch
                  size="small"
                  checked={row.active}
                  loading={toggle.isPending}
                  onChange={(v) => toggle.mutate({ id: row.id, active: v })}
                />
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          役職マスタ
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" onClick={() => setCreating(true)}>
            役職を作る
          </Button>
        )}
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          上長の手動指定が無い人は、所属をさかのぼって「自分より rank
          が小さい人」のうち一番近い人に繋がる。並びを変えると指揮系統図も変わる。
        </Typography.Text>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="役職を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<Position>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={false}
      />

      <Modal
        open={open}
        title={editing ? `役職の編集(${editing.name})` : "役職を作る"}
        okText="保存"
        cancelText="やめる"
        confirmLoading={save.isPending}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => {
          void form.validateFields().then((v) => save.mutate(v));
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="役職名" rules={[{ required: true, message: "役職名は必須" }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="rank" label="rank" initialValue={100}>
            <InputNumber style={{ width: "100%" }} min={0} max={9999} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
